<template>
  <div ref="app" data-app>
    <AppShell />
    <AppGlobalDialogs />
  </div>
</template>

<script setup lang="ts">
import { nextTick, onMounted, onUnmounted, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import type { SessionInformation } from "@wkt/api/gwt/gwtDTO";
import AppGlobalDialogs from "@wkt/components/shell/AppGlobalDialogs.vue";
import { useAlert } from "@wkt/composables/useAlert";
import { useAppUpdateMonitor } from "@wkt/composables/useAppUpdateMonitor";
import { useBadges } from "@wkt/composables/useBadges";
import { useCordovaBridge } from "@wkt/composables/useCordovaBridge";
import AppShell from "@wkt/components/shell/AppShell.vue";
import { usePrimeVueLocale } from "@wkt/composables/usePrimeVueLocale";
import { useWebSocket } from "@wkt/composables/useWebSocket";
import { showForegroundPushNotification } from "@wkt/composables/useFirebaseMessaging";
import {
  getKeycloakProfile,
  login as startKeycloakLogin,
} from "@wkt/lib/keycloak";
import {
  clearKioskEmployeeId,
  getKioskEmployeeId,
} from "@wkt/lib/auth-context";
import { router } from "@wkt/router";
import type { RouteLocationRaw } from "vue-router";
import { SERVICE_VERSION_UPDATED_EVENT } from "@wkt/lib/httpClient";
import {
  getRevealPayloadFromRoute,
  getShellModeForRouteName,
  getVueModuleForRouteName,
  normalizeLegacyHash,
  stripAuthResponseFragment,
} from "@wkt/router/moduleRegistry";
import { useAppDialogsStore } from "@wkt/stores/appDialogs";
import {
  type BootstrapResult,
  useSessionCatalogStore,
} from "@wkt/stores/sessionCatalog";
import { useCheckinStore } from "@wkt/stores/checkin";
import { useHomeStore } from "@wkt/stores/home";
import { useShellStore } from "@wkt/stores/shell";
import { isAppRuntime, isPhoneRuntime, isTabletRuntime } from "@wkt/lib/device";

const { t } = useI18n();
const { showAlert } = useAlert();
const { connect, disconnect } = useWebSocket();
const cordovaBridge = useCordovaBridge();
const appUpdateMonitor = useAppUpdateMonitor();
const { computeFromEmployee, refreshBadges, refreshDocumentsBadge } =
  useBadges();
const appDialogsStore = useAppDialogsStore();
const sessionCatalogStore = useSessionCatalogStore();
const checkinStore = useCheckinStore();
const homeStore = useHomeStore();
const shellStore = useShellStore();

usePrimeVueLocale();

const app = ref<HTMLElement | null>(null);

let isSyncingLegacyHash = false;
let bootstrappedSessionKey: string | null = null;
let bootstrapDirectRoutePath: string | null = null;
let connectedSocketUsername: string | null = null;
let disposeNativeDeviceListener: (() => void) | null = null;
let disposeNativeProcessUrlListener: (() => void) | null = null;
let disposeNotificationTokenListener: (() => void) | null = null;
let disposeDeviceUuidListener: (() => void) | null = null;
let disposeServiceVersionListener: (() => void) | null = null;
let disposePeriodClosedListener: (() => void) | null = null;
let lastSyncedGeofencePayload: string | null = null;
let pendingDeviceRegistrationFromToken = false;
let lastActivityFinishFeedbackAt = 0;
const recentActivityFinishFeedbackKeys = new Map<string, number>();
const legacyGlobalShellHostRuntime = false;

const DEFAULT_PRIMARY_COLOR = "#185EA9";
const DEFAULT_PRIMARY_DARK_COLOR = "#0056b3";
const DEFAULT_SECONDARY_COLOR = "#6c757d";
const DEFAULT_PRIMARY_RGB = "24, 94, 169";
const DEFAULT_SHELL_COLOR = DEFAULT_PRIMARY_COLOR;
const DEFAULT_SHELL_RGB = DEFAULT_PRIMARY_RGB;

function getDirectVueHashPath(
  hash = window.location.hash || "",
): string | null {
  const sanitizedHash = stripAuthResponseFragment(hash);

  if (!sanitizedHash.startsWith("#/")) {
    return null;
  }

  if (sanitizedHash === "#/" || sanitizedHash === "#") {
    return "/";
  }

  return sanitizedHash.substring(1);
}

function resolveSocketUsername(): string | null {
  const keycloakUsername = getKeycloakProfile()?.username;
  if (typeof keycloakUsername === "string" && keycloakUsername.trim()) {
    return keycloakUsername.trim();
  }

  const sessionInformation = sessionCatalogStore.peekSessionInformation();
  const employeeUsername = sessionInformation?.employee?.username;
  return typeof employeeUsername === "string" && employeeUsername.trim()
    ? employeeUsername.trim()
    : null;
}

function normalizeColor(color?: string | null): string | null {
  if (!color) {
    return null;
  }

  const normalized = color.trim();
  return normalized.length > 0 ? normalized : null;
}

function colorToRgbTriplet(color?: string | null): string | null {
  const normalized = normalizeColor(color);
  if (!normalized) {
    return null;
  }

  const hex = normalized.replace("#", "");
  if (/^[0-9a-fA-F]{3}$/.test(hex)) {
    const expanded = hex
      .split("")
      .map((char) => `${char}${char}`)
      .join("");
    const red = Number.parseInt(expanded.substring(0, 2), 16);
    const green = Number.parseInt(expanded.substring(2, 4), 16);
    const blue = Number.parseInt(expanded.substring(4, 6), 16);
    return `${red}, ${green}, ${blue}`;
  }

  if (/^[0-9a-fA-F]{6}$/.test(hex)) {
    const red = Number.parseInt(hex.substring(0, 2), 16);
    const green = Number.parseInt(hex.substring(2, 4), 16);
    const blue = Number.parseInt(hex.substring(4, 6), 16);
    return `${red}, ${green}, ${blue}`;
  }

  const rgbMatch = normalized.match(
    /^rgba?\(\s*(\d{1,3})\s*,\s*(\d{1,3})\s*,\s*(\d{1,3})/i,
  );
  if (rgbMatch) {
    return `${rgbMatch[1]}, ${rgbMatch[2]}, ${rgbMatch[3]}`;
  }

  return null;
}

function darkenColor(color: string, factor = 0.15): string {
  const rgb = colorToRgbTriplet(color);
  if (!rgb) {
    return DEFAULT_PRIMARY_DARK_COLOR;
  }

  const [red, green, blue] = rgb
    .split(",")
    .map((value) => Number.parseInt(value.trim(), 10));
  const darken = (channel: number) =>
    Math.max(0, Math.min(255, Math.round(channel * (1 - factor))));

  return `rgb(${darken(red)}, ${darken(green)}, ${darken(blue)})`;
}

function updateThemeMetaColor(color: string): void {
  if (typeof document === "undefined") {
    return;
  }

  const upsertMeta = (selector: string, content: string) => {
    const meta = document.head.querySelector<HTMLMetaElement>(selector);
    if (meta) {
      meta.setAttribute("content", content);
      return;
    }

    const created = document.createElement("meta");
    if (selector.includes('name="')) {
      created.setAttribute(
        "name",
        selector.match(/name="([^"]+)"/)?.[1] ?? "theme-color",
      );
    }
    created.setAttribute("content", content);
    document.head.appendChild(created);
  };

  upsertMeta('meta[name="theme-color"]', color);
  upsertMeta('meta[name="msapplication-navbutton-color"]', color);
}

function applyThemeVariables(
  sessionInformation: SessionInformation | null,
): void {
  const company = sessionInformation?.company;
  const primaryColor =
    company?.themeEnabled && normalizeColor(company.themePrimaryColor)
      ? normalizeColor(company.themePrimaryColor)!
      : DEFAULT_PRIMARY_COLOR;
  const secondaryColor =
    company?.themeEnabled && normalizeColor(company.themeSecondaryColor)
      ? normalizeColor(company.themeSecondaryColor)!
      : DEFAULT_SECONDARY_COLOR;
  const shellColor =
    company?.themeEnabled && normalizeColor(company.themeHeaderColor)
      ? normalizeColor(company.themeHeaderColor)!
      : primaryColor || DEFAULT_SHELL_COLOR;
  const primaryRgb = colorToRgbTriplet(primaryColor) || DEFAULT_PRIMARY_RGB;
  const primaryDarkColor = darkenColor(primaryColor);
  const shellRgb = colorToRgbTriplet(shellColor) || DEFAULT_SHELL_RGB;
  const shellDarkColor = darkenColor(shellColor);

  const targets = [
    document.documentElement,
    document.body,
    window.$vueWrapper,
    window.$vueWrapperParent,
    app.value,
  ].filter(Boolean) as HTMLElement[];

  targets.forEach((target) => {
    target.style.setProperty("--primary-color", primaryColor);
    target.style.setProperty("--primary-rgb", primaryRgb);
    target.style.setProperty("--primary-dark-color", primaryDarkColor);
    target.style.setProperty("--secondary-color", secondaryColor);
    target.style.setProperty("--shell-color", shellColor);
    target.style.setProperty("--shell-rgb", shellRgb);
    target.style.setProperty("--shell-dark-color", shellDarkColor);
  });

  updateThemeMetaColor(shellColor);
}

function getCurrentModule(): string {
  return (
    getVueModuleForRouteName(router.currentRoute.value.name) ??
    shellStore.pendingRevealModule ??
    shellStore.currentBridgeModule ??
    ""
  );
}

function hasActiveVueModule(): boolean {
  return getCurrentModule().trim().length > 0;
}

type NativePushData = {
  type?: string | null;
  payload?: string | null;
  title?: string | null;
  body?: string | null;
  image?: string | null;
  click?: string | null;
};

type GeofenceMessagePayload = {
  id: string;
  latitude: number;
  longitude: number;
  radius: number;
  transitionType: number;
  notification: {
    id: number;
    title: string;
    text: string;
    openAppOnClick: boolean;
    frequency: number;
  };
};

function parseNativePushEvent(event: string): NativePushData | null {
  if (!event.startsWith("NE_PUSH:")) {
    return null;
  }

  const rawPayload = event.substring("NE_PUSH:".length).trim();
  if (!rawPayload) {
    return null;
  }

  try {
    const parsedPayload = JSON.parse(rawPayload) as {
      data?: NativePushData | null;
      additionalData?: NativePushData | null;
    };

    return parsedPayload.data ?? parsedPayload.additionalData ?? null;
  } catch (error) {
    console.warn("Unable to parse NE_PUSH payload", error);
    return null;
  }
}

function serializeCordovaPayload(payload: unknown): string {
  return JSON.stringify(payload).replace(/"/g, "'");
}

function buildActiveGeofencePayload(): GeofenceMessagePayload | null {
  if (!isAppRuntime()) {
    return null;
  }

  const employee = sessionCatalogStore.employee;
  const workplace = sessionCatalogStore.sessionInformation?.workplace ?? null;

  if (
    employee?.geofenceEnabled !== true ||
    workplace?.geofenceEnabled !== true ||
    workplace.id == null ||
    workplace.geofenceLatitude == null ||
    workplace.geofenceLongitude == null ||
    workplace.geofenceRadius == null
  ) {
    return null;
  }

  const hasActiveWorkday =
    Boolean(checkinStore.currentWorkday?.currentActivityStart) &&
    checkinStore.currentWorkday?.closed !== true;

  return {
    id: `${workplace.id}-${hasActiveWorkday ? "out" : "in"}`,
    latitude: workplace.geofenceLatitude,
    longitude: workplace.geofenceLongitude,
    radius: Math.round(workplace.geofenceRadius),
    transitionType: hasActiveWorkday ? 2 : 1,
    notification: {
      id: hasActiveWorkday ? 2 : 1,
      title: "tweem",
      text: hasActiveWorkday
        ? t("wkt.client.geofence-service.remember_finish_workday")
        : t("wkt.client.geofence-service.remember_start_workday"),
      openAppOnClick: true,
      frequency: 9000,
    },
  };
}

function syncGeofences(): void {
  if (!isAppRuntime()) {
    lastSyncedGeofencePayload = null;
    return;
  }

  const payload = buildActiveGeofencePayload();
  const serializedPayload = payload ? serializeCordovaPayload(payload) : null;

  if (serializedPayload === lastSyncedGeofencePayload) {
    return;
  }

  cordovaBridge.removeAllGeofences();

  if (serializedPayload) {
    cordovaBridge.addOrUpdateGeofence(serializedPayload);
  }

  lastSyncedGeofencePayload = serializedPayload;
}

function refreshModule(): void {
  shellStore.requestRefresh();
}

function syncVueWrapperHost(): void {
  return;
}

function syncVueWrapperLayout(): void {
  if (!window.$vueWrapper) {
    return;
  }

  window.$vueWrapper.style.position = "relative";
  window.$vueWrapper.style.removeProperty("inset");
  window.$vueWrapper.style.width = "100%";
  window.$vueWrapper.style.height = "100%";
  window.$vueWrapper.style.minHeight = "100%";
  window.$vueWrapper.style.removeProperty("z-index");
  window.$vueWrapper.style.pointerEvents = "auto";
  window.$vueWrapper.style.removeProperty("overflow");
}

function syncLegacyShellChromeClass(routeName?: unknown): void {
  const shouldHideLegacyChrome =
    legacyGlobalShellHostRuntime &&
    getShellModeForRouteName(routeName) === "full";

  document.body.classList.toggle(
    "wkt-vue-shell-chrome-active",
    shouldHideLegacyChrome,
  );
}

function syncVueWrapperVisibility(routeName?: unknown): void {
  if (!window.$vueWrapper) {
    return;
  }

  syncVueWrapperLayout();

  const shouldShowWrapper =
    typeof routeName !== "string" ||
    routeName !== "bridge-root" ||
    shellStore.pendingRevealModule !== null;

  window.$vueWrapper.style.display = shouldShowWrapper ? "block" : "none";
}

function setGlobalShellHostEnabled(enabled: boolean): void {
  const normalized = legacyGlobalShellHostRuntime && enabled === true;
  shellStore.setGlobalShellHostEnabled(normalized);
  syncVueWrapperHost();
  syncVueWrapperLayout();
  syncVueWrapperVisibility(shellStore.activeRouteName);
  syncLegacyShellChromeClass(shellStore.activeRouteName);
}

function getNormalizedLegacyPath(): string {
  const hash = stripAuthResponseFragment(window.location.hash || "");
  const directVueHashPath = getDirectVueHashPath(hash);

  if (directVueHashPath) {
    return directVueHashPath;
  }

  if (hash === "" || hash === "#") {
    return "/";
  }

  const normalizedHash = normalizeLegacyHash(window.location.hash);
  return normalizedHash ? normalizedHash.substring(1) : "/";
}

function syncBrowserHashWithAuthSanitizer(): void {
  const currentHash = window.location.hash || "";
  const sanitizedHash = stripAuthResponseFragment(currentHash);

  if (!sanitizedHash || sanitizedHash === currentHash) {
    return;
  }

  const nextUrl = `${window.location.pathname}${window.location.search}${sanitizedHash}`;
  window.history.replaceState(window.history.state, document.title, nextUrl);
}

function hasLoginRequiredErrorFragment(): boolean {
  const hash = window.location.hash || "";
  return /(?:^|&)error=login_required(?:&|$)/i.test(hash.substring(1));
}

function extractHashFromProcessUrl(url: string): string | null {
  const normalizedUrl = url.trim();
  if (!normalizedUrl) {
    return null;
  }

  if (normalizedUrl.startsWith("#")) {
    return normalizedUrl;
  }

  if (normalizedUrl.startsWith("/#")) {
    return normalizedUrl.substring(1);
  }

  try {
    const parsedUrl = new URL(normalizedUrl, window.location.href);
    if (parsedUrl.hash) {
      return parsedUrl.hash;
    }

    const pathWithSearch = `${parsedUrl.pathname}${parsedUrl.search}`;
    if (pathWithSearch && pathWithSearch !== "/") {
      return `#${pathWithSearch}`;
    }
  } catch (_) {}

  if (normalizedUrl.startsWith("/")) {
    return `#${normalizedUrl}`;
  }

  const hashIndex = normalizedUrl.indexOf("#");
  if (hashIndex >= 0) {
    return normalizedUrl.substring(hashIndex);
  }

  return /^[a-z0-9_-]+(?:[;/].*)?$/i.test(normalizedUrl)
    ? `#${normalizedUrl}`
    : null;
}

function normalizeProcessUrl(url: string): string | null {
  const extractedHash = extractHashFromProcessUrl(url);
  if (!extractedHash) {
    return null;
  }

  if (extractedHash.startsWith("#/")) {
    return extractedHash;
  }

  return normalizeLegacyHash(extractedHash) ?? extractedHash;
}

function resolveVueManagedProcessUrl(url: string): string | null {
  const normalizedHash = normalizeProcessUrl(url);
  if (!normalizedHash || !normalizedHash.startsWith("#/")) {
    return null;
  }

  const resolvedRoute = router.resolve(normalizedHash.substring(1));
  if (!resolvedRoute.name || resolvedRoute.name === "bridge-root") {
    return null;
  }

  return normalizedHash;
}

function handleNativeProcessUrl(url: string): void {
  const normalizedHash = resolveVueManagedProcessUrl(url);
  if (!normalizedHash) {
    console.warn(`Unable to resolve Vue-managed PROCESS_URL payload: ${url}`);
    return;
  }

  if (window.location.hash === normalizedHash) {
    handleHashChange();
    return;
  }

  window.location.hash = normalizedHash;
}

function handleNativeBack(): void {
  if (!hasActiveVueModule()) {
    return;
  }

  if (
    window.location.hash &&
    window.location.hash !== "#" &&
    window.history.length > 1
  ) {
    window.history.back();
  }
}

function navigateForNativePush(routeName?: string | null): void {
  if (!routeName) {
    refreshModule();
    return;
  }

  if (router.currentRoute.value.name === routeName) {
    refreshModule();
    return;
  }

  navigateWhenBridgeReady({
    name: routeName,
    query: {
      refresh: "true",
      nonce: String(Date.now()),
    },
  });
}

function isActivityFinishPush(pushPayload: string): boolean {
  return pushPayload === "finish-activity" || pushPayload === "activity-finish";
}

function isAutoStopActivityPush(pushPayload: string): boolean {
  return pushPayload === "auto-stop-activity";
}

function getAutoStopActivityTitle(): string {
  return t("wkt.client.checkin.activity_stopped_by_schedule_title");
}

function getAutoStopActivityMessage(): string {
  return t("wkt.client.checkin.activity_stopped_by_schedule");
}

function matchesAutoStopActivityCopy(pushData: NativePushData | null): boolean {
  return (
    pushData?.title?.trim() === getAutoStopActivityTitle() &&
    pushData?.body?.trim() === getAutoStopActivityMessage()
  );
}

function shouldTreatAsAutoStopActivity(
  pushPayload: string,
  pushData: NativePushData | null,
): boolean {
  return isAutoStopActivityPush(pushPayload) ||
    (isActivityFinishPush(pushPayload) && matchesAutoStopActivityCopy(pushData));
}

function getActivityFinishNotificationPayload(
  pushData: NativePushData | null,
): string {
  const normalizedPayload = pushData?.payload?.trim().toLowerCase() || "";
  return isAutoStopActivityPush(normalizedPayload)
    ? "auto-stop-activity"
    : "activity-finish";
}

function getActivityFinishFeedbackKey(pushData: NativePushData | null): string {
  return [
    "activity-finish",
    pushData?.title?.trim() || getAutoStopActivityTitle(),
    pushData?.body?.trim() || getAutoStopActivityMessage(),
    pushData?.click?.trim() || "",
  ].join("|");
}

function shouldShowActivityFinishFeedback(pushData: NativePushData | null): boolean {
  const feedbackKey = getActivityFinishFeedbackKey(pushData);
  const now = Date.now();
  const lastShownAt = recentActivityFinishFeedbackKeys.get(feedbackKey);

  if (lastShownAt && now - lastShownAt < 60000) {
    return false;
  }

  recentActivityFinishFeedbackKeys.set(feedbackKey, now);
  window.setTimeout(() => {
    recentActivityFinishFeedbackKeys.delete(feedbackKey);
  }, 60000);

  return true;
}

function showActivityFinishBrowserNotification(pushData: NativePushData | null): void {
  void showForegroundPushNotification({
    data: {
      type: "update",
      payload: getActivityFinishNotificationPayload(pushData),
      title: pushData?.title?.trim() || getAutoStopActivityTitle(),
      body: pushData?.body?.trim() || getAutoStopActivityMessage(),
      image: pushData?.image?.trim() || undefined,
      click: pushData?.click?.trim() || undefined,
    },
  });
}

function showActivityFinishFeedback(pushData: NativePushData | null): void {
  if (!shouldShowActivityFinishFeedback(pushData)) {
    return;
  }

  showActivityFinishBrowserNotification(pushData);

  if (isPhoneRuntime() || isTabletRuntime()) {
    return;
  }

  const now = Date.now();
  if (now - lastActivityFinishFeedbackAt < 5000) {
    return;
  }

  lastActivityFinishFeedbackAt = now;
  showAlert({
    title: getAutoStopActivityTitle(),
    text: getAutoStopActivityMessage(),
    color: "warning",
    group: "push",
    styleClass: "wkt-push-toast-message",
    timeout: 5000,
  });
}

async function handleNativePush(event: string): Promise<void> {
  const pushData = parseNativePushEvent(event);
  const pushPayload = pushData?.payload?.trim().toLowerCase() || "";
  const pushType = pushData?.type?.trim().toLowerCase() || "";

  if (shouldTreatAsAutoStopActivity(pushPayload, pushData)) {
    showActivityFinishFeedback(pushData);
    await checkinStore.refreshWorkday();
    return;
  }

  if (isActivityFinishPush(pushPayload)) {
    await checkinStore.refreshWorkday();
    return;
  }

  if (!hasActiveVueModule()) {
    return;
  }

  if (pushPayload === "survey-update") {
    await Promise.allSettled([homeStore.refreshSurveys(), refreshBadges()]);
    navigateForNativePush("home");
    return;
  }

  if (pushPayload === "news-update") {
    await Promise.allSettled([
      homeStore.refreshMessages(),
      homeStore.refreshNews(),
      refreshBadges(),
    ]);
    navigateForNativePush("home");
    return;
  }

  if (pushPayload === "user-documents-update") {
    await Promise.allSettled([homeStore.refreshMessages(), refreshBadges()]);
    const currentRouteName = router.currentRoute.value.name;
    if (currentRouteName === "periodclosurehistory") {
      return;
    }
    navigateForNativePush("documents");
    return;
  }

  if (pushPayload === "user-vacations-update") {
    await Promise.allSettled([
      checkinStore.refreshVacationData(),
      refreshBadges(),
    ]);
    navigateForNativePush("vacations");
    return;
  }

  if (pushPayload === "user-procedures-update") {
    await Promise.allSettled([homeStore.refreshMessages(), refreshBadges()]);
    navigateForNativePush("procedures");
    return;
  }

  if (pushPayload === "user-messages-update") {
    await Promise.allSettled([homeStore.refreshMessages(), refreshBadges()]);
    return;
  }

  if (pushType === "profile-picture-update") {
    const employee = sessionCatalogStore.employee;
    if (employee) {
      employee.profilePicture = pushData?.payload ?? employee.profilePicture;
    }
    if (sessionCatalogStore.sessionInformation?.employee) {
      sessionCatalogStore.sessionInformation.employee.profilePicture =
        pushData?.payload ??
        sessionCatalogStore.sessionInformation.employee.profilePicture;
    }
    return;
  }

  await checkinStore.refreshWorkday();
}

async function handleNativeFileDownloadEvent(event: string): Promise<void> {
  if (!hasActiveVueModule()) {
    return;
  }

  if (event === "NE_FILE_DOWNLOAD_START") {
    return;
  }

  if (event === "NE_FILE_DOWNLOAD_SUCCESS") {
    await Promise.allSettled([homeStore.refreshMessages(), refreshBadges()]);
    return;
  }

  if (event === "NE_FILE_DOWNLOAD_ERROR") {
    showAlert({
      text: t("wkt.client.main.document-download-error"),
      color: "error",
    });
  }
}

async function handleNativeAwake(event: string): Promise<void> {
  if (!hasActiveVueModule()) {
    return;
  }

  const sleepTime = Number.parseFloat(event.substring("NE_AWAKE:".length));
  if (
    !Number.isFinite(sleepTime) ||
    !sessionCatalogStore.peekSessionInformation()
  ) {
    return;
  }

  const pendingTasks: Promise<unknown>[] = [];

  if (sleepTime > 60) {
    pendingTasks.push(checkinStore.refreshWorkday());
  }

  if (sleepTime > 300) {
    pendingTasks.push(refreshBadges());
  }

  if (pendingTasks.length > 0) {
    await Promise.allSettled(pendingTasks);
  }

  cordovaBridge.setAppIconBadge(shellStore.appIconBadgeTotal);
}

function handleNativeDeviceEvent(event: string): void {
  if (event === "NE_FILE_DOWNLOAD_START") {
    void handleNativeFileDownloadEvent(event);
    return;
  }

  if (event === "NE_FILE_DOWNLOAD_SUCCESS") {
    void handleNativeFileDownloadEvent(event);
    return;
  }

  if (event === "NE_FILE_DOWNLOAD_ERROR") {
    void handleNativeFileDownloadEvent(event);
    return;
  }

  if (event.startsWith("NE_BACK")) {
    handleNativeBack();
    return;
  }

  if (event.startsWith("NE_AWAKE:")) {
    void handleNativeAwake(event);
    return;
  }

  if (event.startsWith("NE_PUSH:")) {
    void handleNativePush(event);
  }
}

function registerDeviceWhenSessionReady(): void {
  if (!sessionCatalogStore.isReady) {
    pendingDeviceRegistrationFromToken = true;
    return;
  }

  pendingDeviceRegistrationFromToken = false;
  void sessionCatalogStore.registerDevice();
}

function syncRouteFromLegacyHash(): void {
  const nextPath = getNormalizedLegacyPath();
  const currentPath =
    router.currentRoute.value.fullPath &&
    router.currentRoute.value.fullPath !== ""
      ? router.currentRoute.value.fullPath
      : "/";

  if (currentPath === nextPath) {
    return;
  }

  isSyncingLegacyHash = true;
  navigateWhenBridgeReady(nextPath, true);
}

function handleBootstrapResult(result: BootstrapResult): void {
  const currentRouteName = router.currentRoute.value.name;
  const currentPath = router.currentRoute.value.path;
  const normalizedLegacyPath = getNormalizedLegacyPath();
  const hasLoginError = hasLoginRequiredErrorFragment();

  switch (result.outcome) {
    case "ready":
      syncBrowserHashWithAuthSanitizer();
      if (bootstrapDirectRoutePath) {
        bootstrapLegacyPlaceForDirectRoute();
        return;
      }

      if (
        normalizedLegacyPath !== "/" &&
        (currentPath === "/" || currentRouteName === "bridge-root")
      ) {
        syncRouteFromLegacyHash();
        return;
      }

      if (currentPath === "/" || currentRouteName === "bridge-root") {
        navigateWhenBridgeReady({ name: "home" }, true);
      }
      return;
    case "kiosk":
      syncBrowserHashWithAuthSanitizer();
      if (currentRouteName !== "kiosk") {
        navigateWhenBridgeReady({ name: "kiosk" }, true);
      }
      return;
    case "register":
      syncBrowserHashWithAuthSanitizer();
      if (currentRouteName !== "register") {
        navigateWhenBridgeReady({ name: "register" }, true);
      }
      return;
    case "login-required":
      if (
        !legacyGlobalShellHostRuntime &&
        !hasLoginError &&
        currentRouteName !== "register" &&
        currentRouteName !== "kiosk"
      ) {
        void startKeycloakLogin();
      }
      return;
    case "error":
      return;
  }
}

function maybeResolveStandaloneRootRoute(): void {
  if (legacyGlobalShellHostRuntime) {
    return;
  }

  const currentRoute = router.currentRoute.value;
  const currentHash = window.location.hash || "";
  const isStandaloneRootHash =
    currentHash === "" || currentHash === "#" || currentHash === "#/";

  if (
    sessionCatalogStore.isReady &&
    currentRoute.name === "bridge-root" &&
    currentRoute.path === "/" &&
    isStandaloneRootHash
  ) {
    navigateWhenBridgeReady({ name: "home" }, true);
  }
}

async function syncSessionCatalogWhenReady(): Promise<void> {
  cordovaBridge.setInitProgress("initSession");

  try {
    const result = await sessionCatalogStore.bootstrapSession();
    applyThemeVariables(
      "sessionInformation" in result
        ? result.sessionInformation
        : sessionCatalogStore.peekSessionInformation(),
    );
    handleBootstrapResult(result);
  } finally {
    cordovaBridge.hidePreloader();
  }
}

function navigateWhenBridgeReady(
  target: RouteLocationRaw | string,
  replace = false,
): void {
  const navigation = replace ? router.replace(target) : router.push(target);
  void navigation
    .catch(() => undefined)
    .finally(() => {
      if (replace) {
        isSyncingLegacyHash = false;
      }
    });
}

function maybeBootstrapGlobalDialogs(
  sessionInformation: SessionInformation | null,
): void {
  const employeeId = sessionInformation?.employee?.id ?? null;
  const companyId = sessionInformation?.company?.id ?? null;
  const sessionKey =
    employeeId != null && companyId != null
      ? `${companyId}:${employeeId}`
      : null;

  if (!employeeId || !sessionKey || sessionKey === bootstrappedSessionKey) {
    return;
  }

  bootstrappedSessionKey = sessionKey;

  const pendingTerms = sessionInformation?.pendingTerms ?? [];
  if (pendingTerms.length > 0) {
    appDialogsStore.openPendingTerms(pendingTerms);
    return;
  }

  const featureMessages = sessionInformation?.featureMessages ?? [];
  const lastFeatureMessage = featureMessages[featureMessages.length - 1];
  const lastClientMessageId =
    sessionInformation?.employee?.lastClientMessageId ?? null;

  if (
    lastFeatureMessage?.id != null &&
    lastClientMessageId !== lastFeatureMessage.id
  ) {
    appDialogsStore.openFeatureMessages(featureMessages);
  }
}

function bootstrapLegacyPlaceForDirectRoute(): void {
  if (!bootstrapDirectRoutePath) {
    return;
  }

  const resolvedRoute = router.resolve(bootstrapDirectRoutePath);
  const expectedModule = getVueModuleForRouteName(resolvedRoute.name);
  if (!expectedModule) {
    return;
  }

  if (
    bootstrapVueModuleForDirectRoute(expectedModule, resolvedRoute.fullPath)
  ) {
    return;
  }

  console.warn(
    `Could not bootstrap Vue direct route ${bootstrapDirectRoutePath}`,
  );
}

function bootstrapVueModuleForDirectRoute(
  module: string,
  resolvedFullPath: string,
): boolean {
  if (!bootstrapDirectRoutePath) {
    return false;
  }

  const resolvedRoute = router.resolve(bootstrapDirectRoutePath);
  const targetRouteName =
    typeof resolvedRoute.name === "string" ? resolvedRoute.name : null;
  const targetShellMode = getShellModeForRouteName(targetRouteName);

  shellStore.queueReveal(
    module,
    getRevealPayloadFromRoute(
      module,
      resolvedRoute as Parameters<typeof getRevealPayloadFromRoute>[1],
    ),
  );
  shellStore.setActiveShellMode(targetShellMode);
  setGlobalShellHostEnabled(targetShellMode === "full");
  syncLegacyShellChromeClass(targetRouteName);
  applyThemeVariables(sessionCatalogStore.peekSessionInformation());

  if (window.$vueWrapper) {
    window.$vueWrapper.style.display = "block";
  }

  void navigateWhenBridgeReady(resolvedFullPath, true);

  nextTick(() => {
    nextTick(() => {
      if (
        bootstrapDirectRoutePath &&
        router.currentRoute.value.fullPath === bootstrapDirectRoutePath &&
        getCurrentModule() === module
      ) {
        bootstrapDirectRoutePath = null;
      }
    });
  });

  return true;
}

function handleHashChange(): void {
  if (!legacyGlobalShellHostRuntime) {
    return;
  }

  syncBrowserHashWithAuthSanitizer();

  const directVueHashPath = getDirectVueHashPath();
  if (directVueHashPath) {
    bootstrapDirectRoutePath = directVueHashPath;
    bootstrapLegacyPlaceForDirectRoute();
  }

  syncRouteFromLegacyHash();
}

function resetPersistedKioskSessionOnColdStart(): void {
  if (!getKioskEmployeeId()) {
    return;
  }

  clearKioskEmployeeId();
  shellStore.syncKioskFromStorage();
  bootstrapDirectRoutePath = null;

  if (router.currentRoute.value.name !== "kiosk") {
    navigateWhenBridgeReady({ name: "kiosk" }, true);
  }
}

onMounted(() => {
  bootstrapDirectRoutePath = getDirectVueHashPath();
  resetPersistedKioskSessionOnColdStart();
  if (legacyGlobalShellHostRuntime && bootstrapDirectRoutePath) {
    bootstrapLegacyPlaceForDirectRoute();
  }
  shellStore.syncKioskFromStorage();
  shellStore.setGlobalShellHostEnabled(legacyGlobalShellHostRuntime);
  syncLegacyShellChromeClass(router.currentRoute.value.name);

  disposeNativeDeviceListener = cordovaBridge.subscribe<string>(
    "native-device",
    handleNativeDeviceEvent,
  );
  disposeNativeProcessUrlListener = cordovaBridge.subscribe<string>(
    "native-process-url",
    handleNativeProcessUrl,
  );
  disposeNotificationTokenListener = cordovaBridge.subscribe<string>(
    "notification-token",
    registerDeviceWhenSessionReady,
  );
  disposeDeviceUuidListener = cordovaBridge.subscribe<string>(
    "device-uuid",
    registerDeviceWhenSessionReady,
  );
  if (legacyGlobalShellHostRuntime) {
    syncRouteFromLegacyHash();
  }
  void syncSessionCatalogWhenReady().finally(() => {
    cordovaBridge.sendMessage("INIT_COMPLETED");
  });
  appUpdateMonitor.start();
  if (legacyGlobalShellHostRuntime) {
    window.addEventListener("hashchange", handleHashChange);
  }
  cordovaBridge.readyToProcessUrl();
  cordovaBridge.readyToProcessNotifications();
  const serviceVersionHandler = () => {
    appDialogsStore.openApplicationUpdated();
  };
  window.addEventListener(SERVICE_VERSION_UPDATED_EVENT, serviceVersionHandler);
  disposeServiceVersionListener = () => {
    window.removeEventListener(
      SERVICE_VERSION_UPDATED_EVENT,
      serviceVersionHandler,
    );
  };
  const periodClosedHandler = () => {
    showAlert({
      color: "error",
      text: t("wkt.client.period-closed.mutation_conflict"),
      timeout: 6_000,
    });
  };
  window.addEventListener("wkt:period-closed", periodClosedHandler);
  disposePeriodClosedListener = () => {
    window.removeEventListener("wkt:period-closed", periodClosedHandler);
  };

  nextTick(() => {
    if (app.value) {
      const firstChild = app.value.firstElementChild as HTMLElement | null;
      if (firstChild) {
        firstChild.style.minHeight = "100%";
      }
    }
  });

  applyThemeVariables(sessionCatalogStore.peekSessionInformation());
});

watch(
  () => router.currentRoute.value.name,
  (routeName) => {
    setGlobalShellHostEnabled(getShellModeForRouteName(routeName) === "full");
    syncVueWrapperVisibility(routeName);
    syncLegacyShellChromeClass(routeName);
  },
  { immediate: true },
);

watch(
  () => router.currentRoute.value.fullPath,
  (fullPath) => {
    if (
      bootstrapDirectRoutePath &&
      fullPath !== "/" &&
      fullPath !== bootstrapDirectRoutePath
    ) {
      bootstrapDirectRoutePath = null;
    }

    if (
      legacyGlobalShellHostRuntime &&
      !isSyncingLegacyHash &&
      fullPath === "/"
    ) {
      syncRouteFromLegacyHash();
    }

    maybeResolveStandaloneRootRoute();
  },
);

watch(
  () => sessionCatalogStore.isReady,
  () => {
    maybeResolveStandaloneRootRoute();
    if (sessionCatalogStore.isReady && pendingDeviceRegistrationFromToken) {
      registerDeviceWhenSessionReady();
    }
  },
  { immediate: true },
);

watch(
  () => sessionCatalogStore.sessionInformation,
  (sessionInformation) => {
    applyThemeVariables(sessionInformation);
    computeFromEmployee();
    void refreshDocumentsBadge();
    maybeBootstrapGlobalDialogs(sessionInformation);
  },
);

watch(
  () => shellStore.appIconBadgeTotal,
  (totalBadges) => {
    if (!isAppRuntime()) {
      return;
    }

    cordovaBridge.setAppIconBadge(totalBadges);
  },
  { immediate: true },
);

watch(
  () => [
    sessionCatalogStore.employee?.geofenceEnabled ?? null,
    sessionCatalogStore.sessionInformation?.workplace?.id ?? null,
    sessionCatalogStore.sessionInformation?.workplace?.geofenceEnabled ?? null,
    sessionCatalogStore.sessionInformation?.workplace?.geofenceLatitude ?? null,
    sessionCatalogStore.sessionInformation?.workplace?.geofenceLongitude ??
      null,
    sessionCatalogStore.sessionInformation?.workplace?.geofenceRadius ?? null,
    checkinStore.currentWorkday?.currentActivityStart ?? null,
    checkinStore.currentWorkday?.closed ?? null,
  ],
  () => {
    syncGeofences();
  },
  { immediate: true },
);

watch(
  () => resolveSocketUsername(),
  (username) => {
    if (username === connectedSocketUsername) {
      return;
    }

    if (connectedSocketUsername) {
      disconnect();
      connectedSocketUsername = null;
    }

    if (!username) {
      return;
    }

    connect(username);
    connectedSocketUsername = username;
  },
  { immediate: true },
);

onUnmounted(() => {
  disposeNativeDeviceListener?.();
  disposeNativeDeviceListener = null;
  disposeNativeProcessUrlListener?.();
  disposeNativeProcessUrlListener = null;
  disposeNotificationTokenListener?.();
  disposeNotificationTokenListener = null;
  disposeDeviceUuidListener?.();
  disposeDeviceUuidListener = null;
  disposeServiceVersionListener?.();
  disposeServiceVersionListener = null;
  disposePeriodClosedListener?.();
  disposePeriodClosedListener = null;
  if (legacyGlobalShellHostRuntime) {
    window.removeEventListener("hashchange", handleHashChange);
  }
  document.body.classList.remove("wkt-vue-shell-chrome-active");
  appUpdateMonitor.stop();
  disconnect();
  connectedSocketUsername = null;
});
</script>

<style>
html,
body {
  margin: 0;
  padding: 0;
  height: 100%;
  width: 100%;
  background:
    linear-gradient(180deg, rgba(24, 94, 169, 0.05), transparent 14%), #f7f9fc;
}

[data-app] {
  position: relative;
  width: 100% !important;
  min-height: 100% !important;
  height: 100% !important;
  display: block;
}

[data-app] > * {
  width: 100%;
  min-height: 100%;
}

body.hide-expense-sheet-profiles #mainProfilesPanel,
body.hide-legacy-main-profiles #mainProfilesPanel,
body.hide-expense-sheet-profiles #mainProfilesDropdownPanel,
body.hide-legacy-main-profiles #mainProfilesDropdownPanel {
  display: none !important;
  visibility: hidden !important;
  pointer-events: none !important;
  overflow: hidden !important;
  height: 0 !important;
}

body.hide-legacy-main-footer #mainFooter {
  display: none !important;
}

body.wkt-vue-shell-chrome-active #wkt-main-legacy-background-header,
body.wkt-vue-shell-chrome-active .wkt-main-legacy-profiles-dropdown,
body.wkt-vue-shell-chrome-active #wkt-main-legacy-navbar,
body.wkt-vue-shell-chrome-active .wkt-main-legacy-sidenav,
body.wkt-vue-shell-chrome-active .wkt-main-legacy-footer,
body.wkt-vue-shell-chrome-active #wkt-main-legacy-exit-top,
body.wkt-vue-shell-chrome-active #wkt-training-legacy-header-row,
body.wkt-vue-shell-chrome-active #wkt-training-legacy-content-title,
body.wkt-vue-shell-chrome-active #wkt-training-legacy-mobile-header {
  display: none !important;
}
</style>
